import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { api } from '../api';

export default function SettingsPage() {
  const { currentUser, updateUser, logout, refresh } = useContext(AppContext);
  const navigate = useNavigate();

  const [nickname, setNickname] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileMsg, setProfileMsg] = useState('');
  const [profileError, setProfileError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [allProjects, setAllProjects] = useState([]);
  const [myProjectIds, setMyProjectIds] = useState(new Set());
  const [projectsLoading, setProjectsLoading] = useState(true);

  // API Tokens state
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenScope, setNewTokenScope] = useState('read_write');
  const [creatingToken, setCreatingToken] = useState(false);
  const [createdToken, setCreatedToken] = useState(null); // shown once
  const [tokenError, setTokenError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (currentUser) setNickname(currentUser.nickname || '');
  }, [currentUser]);

  useEffect(() => {
    Promise.all([api.browseProjects(), api.getMyProjects()])
      .then(([all, mine]) => {
        setAllProjects(all);
        setMyProjectIds(new Set(mine.map((m) => m.project_id)));
        setProjectsLoading(false);
      })
      .catch(() => setProjectsLoading(false));
    api.listTokens()
      .then(setTokens)
      .catch(() => {})
      .finally(() => setTokensLoading(false));
  }, []); // eslint-disable-line

  const handleCreateToken = async (e) => {
    e.preventDefault();
    setTokenError('');
    setCreatedToken(null);
    setCopied(false);
    setCreatingToken(true);
    try {
      const result = await api.createToken({ name: newTokenName.trim(), scope: newTokenScope });
      setCreatedToken(result);
      setTokens((prev) => [result, ...prev]);
      setNewTokenName('');
    } catch (err) {
      setTokenError(err.message);
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevokeToken = async (tokenId) => {
    if (!window.confirm('Revoke this token? It will stop working immediately.')) return;
    try {
      await api.revokeToken(tokenId);
      setTokens((prev) => prev.map((t) => t.id === tokenId ? { ...t, is_active: false } : t));
      if (createdToken?.id === tokenId) setCreatedToken(null);
    } catch (err) {
      alert('Failed to revoke: ' + err.message);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileMsg('');
    setProfileError('');
    if (newPassword && newPassword !== confirmPassword) {
      setProfileError('Passwords do not match');
      return;
    }
    setSavingProfile(true);
    try {
      const updates = {};
      if (nickname !== currentUser.nickname) updates.nickname = nickname;
      if (newPassword) updates.password = newPassword;
      if (Object.keys(updates).length === 0) { setProfileMsg('Nothing to change'); setSavingProfile(false); return; }
      const updated = await api.updateMe(updates);
      updateUser({ nickname: updated.nickname });
      setNewPassword('');
      setConfirmPassword('');
      setProfileMsg('Saved!');
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleJoin = async (projectId) => {
    try {
      await api.joinProject(projectId);
      setMyProjectIds((prev) => new Set([...prev, projectId]));
      refresh();
    } catch (err) {
      alert('Failed to join: ' + err.message);
    }
  };

  const handleLeave = async (projectId) => {
    if (!window.confirm('Leave this project?')) return;
    try {
      await api.leaveProject(projectId);
      setMyProjectIds((prev) => { const s = new Set(prev); s.delete(projectId); return s; });
      refresh();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <button className="btn-secondary" onClick={() => navigate('/')}>← Back</button>
        <h2>Settings</h2>
        <button className="btn-danger" onClick={handleLogout}>Log Out</button>
      </div>

      <div className="settings-body">
        {/* Profile section */}
        <section className="settings-section">
          <h3>Profile</h3>
          <div className="settings-info">Email: <strong>{currentUser?.email}</strong></div>
          <form onSubmit={handleProfileSave} className="settings-form">
            <label>Nickname</label>
            <input type="text" value={nickname} onChange={(e) => setNickname(e.target.value)} required />
            <label>New Password <span className="optional">(leave blank to keep current)</span></label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" />
            <label>Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm password" />
            {profileError && <div className="settings-error">{profileError}</div>}
            {profileMsg && <div className="settings-success">{profileMsg}</div>}
            <button type="submit" className="btn-primary" disabled={savingProfile}>
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </section>

        {/* API Tokens section */}
        <section className="settings-section">
          <h3>API Tokens</h3>
          <p className="settings-hint">
            Use API tokens to authenticate MCP clients and scripts without sharing your password.
          </p>

          {/* Create token form */}
          <form onSubmit={handleCreateToken} className="settings-form token-create-form">
            <div className="token-create-row">
              <input
                type="text"
                placeholder="Token name (e.g. MCP Production)"
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                required
                style={{ flex: 1 }}
              />
              <select value={newTokenScope} onChange={(e) => setNewTokenScope(e.target.value)}>
                <option value="read_write">read_write</option>
                <option value="read_only">read_only</option>
              </select>
              <button type="submit" className="btn-primary" disabled={creatingToken || !newTokenName.trim()}>
                {creatingToken ? 'Creating...' : 'Create Token'}
              </button>
            </div>
            {tokenError && <div className="settings-error">{tokenError}</div>}
          </form>

          {/* Newly created token — shown once */}
          {createdToken && (
            <div className="token-reveal">
              <div className="token-reveal-warning">
                ⚠️ Copy this token now — it will not be shown again.
              </div>
              <div className="token-reveal-value">
                <code>{createdToken.token}</code>
                <button className="btn-secondary btn-sm" onClick={() => handleCopy(createdToken.token)}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <div className="token-reveal-hint">
                Add to your MCP config: <code>TCM_API_TOKEN={createdToken.token}</code>
              </div>
            </div>
          )}

          {/* Token list */}
          {tokensLoading ? (
            <div className="tc-empty">Loading tokens...</div>
          ) : tokens.length === 0 ? (
            <div className="tc-empty">No tokens yet</div>
          ) : (
            <table className="token-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Prefix</th>
                  <th>Scope</th>
                  <th>Created</th>
                  <th>Last used</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} className={t.is_active ? '' : 'token-revoked'}>
                    <td>{t.name}</td>
                    <td><code>{t.prefix}…</code></td>
                    <td><span className={`token-scope token-scope-${t.scope}`}>{t.scope}</span></td>
                    <td>{new Date(t.created_at).toLocaleDateString()}</td>
                    <td>{t.last_used_at ? new Date(t.last_used_at).toLocaleDateString() : '—'}</td>
                    <td>{t.is_active ? <span className="token-active">Active</span> : <span className="token-inactive">Revoked</span>}</td>
                    <td>
                      {t.is_active && (
                        <button className="btn-danger btn-sm" onClick={() => handleRevokeToken(t.id)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Projects section */}
        <section className="settings-section">
          <h3>Projects</h3>
          {projectsLoading ? (
            <div className="tc-empty">Loading projects...</div>
          ) : (
            <div className="settings-projects">
              {allProjects.length === 0 && <div className="tc-empty">No projects exist yet</div>}
              {allProjects.map((p) => {
                const isMember = myProjectIds.has(p.id);
                const isOwner = p.is_owner;
                return (
                  <div key={p.id} className="settings-project-row">
                    <span className="settings-project-name">{p.name}</span>
                    <span className="settings-project-status">
                      {isOwner ? 'Owner' : isMember ? 'Member' : ''}
                    </span>
                    {!isMember && (
                      <button className="btn-primary settings-project-btn" onClick={() => handleJoin(p.id)}>
                        Join
                      </button>
                    )}
                    {isMember && !isOwner && (
                      <button className="btn-danger settings-project-btn" onClick={() => handleLeave(p.id)}>
                        Leave
                      </button>
                    )}
                    {isOwner && (
                      <span className="settings-project-owner-tag">You own this</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
